import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { PageHeader, Badge, EmptyState } from "@/components/Common";
import {
  Plus, Calendar as CalendarIcon, Star, MessageSquare, Filter, X, Search,
} from "lucide-react";

const TYPE_TONE = {
  compliment: "emerald",
  complaint: "red",
  suggestion: "blue",
  question: "slate",
  survey: "amber",
  rating: "amber",
};

function formatDateHeader(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });
}

function groupByDate(items) {
  const map = new Map();
  for (const it of items) {
    const day = (it.created_at || "").slice(0, 10) || "unknown";
    if (!map.has(day)) map.set(day, []);
    map.get(day).push(it);
  }
  return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]));
}

export default function ViewFeedback() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const today = new Date().toISOString().slice(0, 10);
  const [filters, setFilters] = useState({
    from: "", to: "", type: "all", branch: "all", q: "",
  });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    customer_name: "", customer_phone: "", branch: "", type: "complaint",
    subject: "", message: "", rating: 0, status: "open",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: f }, { data: b }] = await Promise.all([
        api.get("/feedback"), api.get("/branches"),
      ]);
      setItems(f);
      setBranches(b);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return items.filter((i) => {
      const day = (i.created_at || "").slice(0, 10);
      if (filters.from && day && day < filters.from) return false;
      if (filters.to && day && day > filters.to) return false;
      if (filters.type !== "all" && i.type !== filters.type) return false;
      if (filters.branch !== "all" && (i.branch || "") !== filters.branch) return false;
      if (filters.q) {
        const q = filters.q.toLowerCase();
        const hay = `${i.subject} ${i.message} ${i.customer_name} ${i.branch}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, filters]);

  const grouped = useMemo(() => groupByDate(filtered), [filtered]);

  const stats = useMemo(() => {
    const s = { total: filtered.length, compliment: 0, complaint: 0, suggestion: 0, avgRating: 0 };
    let ratings = 0, ratingCount = 0;
    for (const i of filtered) {
      if (i.type === "compliment") s.compliment++;
      else if (i.type === "complaint") s.complaint++;
      else if (i.type === "suggestion") s.suggestion++;
      if (i.rating) { ratings += Number(i.rating); ratingCount++; }
    }
    s.avgRating = ratingCount ? (ratings / ratingCount).toFixed(1) : "—";
    return s;
  }, [filtered]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/feedback", { ...form, rating: Number(form.rating) || null });
      toast.success("Feedback logged");
      setOpen(false);
      setForm({ customer_name: "", customer_phone: "", branch: "", type: "complaint",
        subject: "", message: "", rating: 0, status: "open" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const clearFilters = () => setFilters({ from: "", to: "", type: "all", branch: "all", q: "" });
  const setPreset = (days) => {
    const to = today;
    const d = new Date(); d.setDate(d.getDate() - days);
    const from = d.toISOString().slice(0, 10);
    setFilters((f) => ({ ...f, from, to }));
  };

  return (
    <div>
      <PageHeader
        eyebrow="Stakeholders"
        title="View Feedback"
        subtitle="Browse customer feedback grouped by date. Filter by period, type or branch — and log new feedback captured over the phone or in person."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-900 hover:bg-emerald-950 text-white"
                data-testid="viewfb-new-button">
                <Plus size={16} className="mr-1.5" /> Log feedback
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display">New feedback entry</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3" data-testid="viewfb-form">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Customer name</Label>
                    <Input value={form.customer_name}
                      onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                      className="mt-1" data-testid="viewfb-customer-input" />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input value={form.customer_phone}
                      onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                      className="mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Type</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                      <SelectTrigger className="mt-1" data-testid="viewfb-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["compliment", "complaint", "suggestion", "question", "survey"].map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Branch</Label>
                    <Select value={form.branch} onValueChange={(v) => setForm({ ...form, branch: v })}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Select branch" /></SelectTrigger>
                      <SelectContent>
                        {branches.map((b) => (<SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Subject</Label>
                  <Input required value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    className="mt-1" data-testid="viewfb-subject-input" />
                </div>
                <div>
                  <Label>Message</Label>
                  <Textarea rows={4} required value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="mt-1" data-testid="viewfb-message-input" />
                </div>
                <div>
                  <Label>Rating</Label>
                  <div className="mt-1 flex gap-1">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button type="button" key={n}
                        onClick={() => setForm({ ...form, rating: n })}
                        className={`h-9 w-9 rounded-md text-lg transition-colors ${
                          n <= Number(form.rating)
                            ? "bg-amber-400 text-amber-950"
                            : "bg-gray-100 text-gray-400 hover:bg-gray-200"
                        }`}
                        data-testid={`viewfb-rating-${n}`}>★</button>
                    ))}
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={busy}
                    className="bg-emerald-900 hover:bg-emerald-950 text-white"
                    data-testid="viewfb-submit-button">
                    {busy ? "Saving…" : "Save feedback"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi label="Total feedback" value={stats.total} tone="emerald" />
        <Kpi label="Complaints" value={stats.complaint} tone="red" />
        <Kpi label="Compliments" value={stats.compliment} tone="emerald" />
        <Kpi label="Avg rating" value={stats.avgRating} icon={<Star size={12} className="fill-amber-400 text-amber-400 inline mr-1" />} tone="amber" />
      </div>

      {/* Filters bar */}
      <div className="bg-white border border-gray-200 rounded-md p-3 mb-4"
        data-testid="viewfb-filters">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider">From</Label>
            <Input type="date" value={filters.from}
              onChange={(e) => setFilters({ ...filters, from: e.target.value })}
              className="mt-1 h-9 w-40" data-testid="viewfb-from-date" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">To</Label>
            <Input type="date" value={filters.to}
              onChange={(e) => setFilters({ ...filters, to: e.target.value })}
              className="mt-1 h-9 w-40" data-testid="viewfb-to-date" />
          </div>
          <div className="flex gap-1.5">
            <PresetChip onClick={() => setPreset(6)}>Last 7d</PresetChip>
            <PresetChip onClick={() => setPreset(29)}>Last 30d</PresetChip>
            <PresetChip onClick={() => setPreset(89)}>Last 90d</PresetChip>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Type</Label>
            <Select value={filters.type} onValueChange={(v) => setFilters({ ...filters, type: v })}>
              <SelectTrigger className="mt-1 h-9 w-36" data-testid="viewfb-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {["compliment", "complaint", "suggestion", "question", "survey", "rating"].map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Branch</Label>
            <Select value={filters.branch} onValueChange={(v) => setFilters({ ...filters, branch: v })}>
              <SelectTrigger className="mt-1 h-9 w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All branches</SelectItem>
                {branches.map((b) => (<SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[180px]">
            <Label className="text-[10px] uppercase tracking-wider">Search</Label>
            <div className="relative mt-1">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                placeholder="Subject, message, customer, branch…"
                className="pl-8 h-9" data-testid="viewfb-search-input" />
            </div>
          </div>
          {(filters.from || filters.to || filters.type !== "all" || filters.branch !== "all" || filters.q) && (
            <Button variant="ghost" size="sm" onClick={clearFilters}
              className="text-red-700 hover:bg-red-50"
              data-testid="viewfb-clear-filters">
              <X size={12} className="mr-1" /> Clear
            </Button>
          )}
        </div>
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
      ) : grouped.length === 0 ? (
        <EmptyState title="No feedback matches your filters"
          hint="Try widening the date range or clearing filters." />
      ) : (
        <div className="space-y-6" data-testid="viewfb-timeline">
          {grouped.map(([day, entries]) => (
            <div key={day}>
              <div className="flex items-center gap-2 mb-3">
                <CalendarIcon size={13} className="text-emerald-800" />
                <div className="text-sm font-display font-semibold text-emerald-900"
                  data-testid={`viewfb-day-header-${day}`}>
                  {day === "unknown" ? "Undated" : formatDateHeader(day)}
                </div>
                <Badge tone="slate">{entries.length}</Badge>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {entries.map((f) => (
                  <div key={f.id}
                    className="bg-white border border-gray-200 rounded-md p-4 hover:shadow-sm transition-shadow"
                    data-testid={`viewfb-card-${f.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <Badge tone={TYPE_TONE[f.type] || "slate"}>{f.type || "feedback"}</Badge>
                      <div className="text-[10px] text-gray-500">
                        {new Date(f.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                    <div className="font-semibold text-gray-900 text-sm leading-snug">{f.subject}</div>
                    <div className="text-xs text-gray-600 mt-1.5 line-clamp-3 whitespace-pre-wrap">{f.message}</div>
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100 text-[11px] text-gray-600 flex-wrap">
                      {f.customer_name && <span className="font-medium">{f.customer_name}</span>}
                      {f.branch && <Badge tone="slate">{f.branch}</Badge>}
                      {f.rating && (
                        <span className="ml-auto flex items-center gap-0.5 text-amber-500">
                          {"★".repeat(Number(f.rating))}
                          <span className="text-gray-400">{"★".repeat(5 - Number(f.rating))}</span>
                        </span>
                      )}
                      {f.source === "public_form" && (
                        <Badge tone="amber">Public form</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, icon, tone = "slate" }) {
  const toneClass = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    red: "bg-red-50 border-red-200 text-red-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    slate: "bg-gray-50 border-gray-200 text-gray-900",
  }[tone];
  return (
    <div className={`border rounded-md p-3 ${toneClass}`}>
      <div className="text-[10px] uppercase tracking-widest font-semibold opacity-70">{label}</div>
      <div className="text-2xl font-display font-bold mt-0.5">{icon}{value}</div>
    </div>
  );
}

function PresetChip({ children, onClick }) {
  return (
    <button type="button" onClick={onClick}
      className="h-9 px-3 rounded-md text-xs bg-emerald-50 border border-emerald-200 text-emerald-900 hover:bg-emerald-100 transition-colors">
      {children}
    </button>
  );
}
